import { useCallback, useRef } from 'react'

import useDialog from './useDialog'

/**
 * Hook that creates a promise-based dialog interface.
 * Returns a function that shows a dialog and returns a promise that resolves based on user action.
 *
 * @param {Object} options
 * @param {React.Component} options.component - The dialog component to render
 * @param {Function} options.getProps - Function that returns props for the dialog component
 * @returns {Function} showDialog - Function that returns a promise resolving with the user's choice
 */
export default function usePromiseDialog({ component, getProps }) {
  const pendingPromiseRef = useRef(null)

  const openDialog = useDialog({
    component,
    props: ({ close }) => {
      const baseProps = getProps()

      return {
        ...baseProps,
        onReject: (...args) => {
          // Call original onReject if it exists
          if (baseProps.onReject) {
            const result = baseProps.onReject(...args)
            if (result instanceof Promise) {
              return result.then((value) => {
                close()
                if (pendingPromiseRef.current) {
                  pendingPromiseRef.current.resolve(value !== undefined ? value : false)
                  pendingPromiseRef.current = null
                }
              })
            }

            // Handle non-promise return value
            close()
            if (pendingPromiseRef.current) {
              pendingPromiseRef.current.resolve(result !== undefined ? result : false)
              pendingPromiseRef.current = null
            }
            return
          }

          close()
          if (pendingPromiseRef.current) {
            pendingPromiseRef.current.resolve(false)
            pendingPromiseRef.current = null
          }
        },
        onConfirm: (...args) => {
          // Call original onConfirm if it exists
          if (baseProps.onConfirm) {
            const result = baseProps.onConfirm(...args)
            if (result instanceof Promise) {
              return result.then((value) => {
                close()
                if (pendingPromiseRef.current) {
                  pendingPromiseRef.current.resolve(value !== undefined ? value : true)
                  pendingPromiseRef.current = null
                }
              })
            }

            // Handle non-promise return value
            close()
            if (pendingPromiseRef.current) {
              pendingPromiseRef.current.resolve(result !== undefined ? result : true)
              pendingPromiseRef.current = null
            }
            return
          }

          close()
          if (pendingPromiseRef.current) {
            pendingPromiseRef.current.resolve(true)
            pendingPromiseRef.current = null
          }
        },
      }
    },
  })

  return useCallback(() => {
    return new Promise((resolve) => {
      pendingPromiseRef.current = { resolve }
      openDialog()
    })
  }, [openDialog])
}
